import type { TRange } from '../utils/pattern.ts'
import type { TCodeHandle } from './CodeBody.tsx'
import type { TFindMode } from './FindBar.tsx'
import { createEffect, createMemo, createSignal, Match, on, onCleanup, onMount, Show, Switch } from 'solid-js'
import { routeToUrl } from '../store/router.ts'
import { searchQuery } from '../store/search.ts'
import { inTextField, useShortcuts } from '../store/shortcuts.ts'
import { currentRef, openFile, openRef, route, useBlame, useFileContent, useRepoDetails } from '../store/view.ts'
import { copy } from '../utils/clipboard.ts'
import { cn } from '../utils/cn.ts'
import { formatSize } from '../utils/format.ts'
import { matchRanges, patternOf } from '../utils/pattern.ts'
import { AYamlView } from './AYamlView.tsx'
import { CodeBody } from './CodeBody.tsx'
import { FileTree } from './FileTree.tsx'
import { FindBar } from './FindBar.tsx'
import { LinksMenu } from './LinksMenu.tsx'
import { PathBreadcrumb } from './PathBreadcrumb.tsx'
import { RefPicker } from './RefPicker.tsx'
import { Splitter } from './Splitter.tsx'
import { Button } from './ui/Button.tsx'
import { WoodpeckerView } from './WoodpeckerView.tsx'

const MIN_FONT = 9
const MAX_FONT = 20
/* code search leaves the code font unset, so it lands on the browser's default
   monospace size - 13px everywhere that matters */
const DEFAULT_FONT = 13
const FONT_KEY = 'codesearch-font-size'

function storedFontSize(): number {
  try {
    return Number(localStorage.getItem(FONT_KEY)) || DEFAULT_FONT
  } catch {
    return DEFAULT_FONT
  }
}

export function FileView() {
  const file = useFileContent()
  const [treeWidth, setTreeWidth] = createSignal(280)
  const [showTree, setShowTree] = createSignal(true)
  const [showRendered, setShowRendered] = createSignal(true)
  const [showMatches, setShowMatches] = createSignal(true)
  const [showBlame, setShowBlame] = createSignal(false)
  const [showComments, setShowComments] = createSignal(true)
  const [columnGuide, setColumnGuide] = createSignal(false)
  const [refPickerOpen, setRefPickerOpen] = createSignal(false)
  const [linksOpen, setLinksOpen] = createSignal(false)
  const [fontSize, setFontSize] = createSignal(storedFontSize())
  const [findMode, setFindMode] = createSignal<TFindMode | null>(null)
  const [findText, setFindText] = createSignal('')
  const [currentMatch, setCurrentMatch] = createSignal(0)

  let code: TCodeHandle | undefined
  let content: HTMLDivElement | undefined
  const target = () => route() as { repo: string, path: string, line?: number }

  const blame = useBlame(showBlame)
  const repo = useRepoDetails(() => target().repo)

  const markdown = () => file.data?.markdown ?? null
  const ayaml = () => (file.data as any)?.ayaml ?? null
  const woodpecker = () => (file.data as any)?.woodpecker ?? null
  const rendered = () => (showRendered() ? (markdown() ? 'markdown' : ayaml() ? 'ayaml' : woodpecker() ? 'woodpecker' : null) : null)

  /*
    what to highlight inside the file: whatever you typed into find-in-file, and
    failing that the query that got you here. arriving from a result with the matches
    already lit is the whole point - code search does the same, and it's the
    difference between "here is the file" and "here is why this file"
  */
  const pattern = createMemo(() => {
    if (findMode() === 'find' && findText()) return patternOf(findText())
    if (!showMatches()) return null
    return patternOf(searchQuery())
  })

  /** line number -> ranges, plus a flat list of (line, index) for stepping through */
  const matches = createMemo(() => {
    const regex = pattern()
    const content = file.data?.content
    const ranges = new Map<number, TRange[]>()
    const flat: { line: number, index: number }[] = []
    if (!regex || !content) return { ranges, flat }

    const lines = content.split('\n')
    for (let i = 0; i < lines.length; i++) {
      const found = matchRanges(lines[i], regex)
      if (found.length === 0) continue
      ranges.set(i + 1, found)
      for (let k = 0; k < found.length; k++) flat.push({ line: i + 1, index: k })
      if (flat.length > 5000) break
    }
    return { ranges, flat }
  })

  const currentHit = () => matches().flat[currentMatch()]

  function step(backwards: boolean) {
    const total = matches().flat.length
    if (total === 0) return
    const next = (currentMatch() + (backwards ? -1 : 1) + total) % total
    setCurrentMatch(next)
    code?.scrollToLine(matches().flat[next].line)
  }

  // a new file (or a new query) starts from the first match again, not from wherever
  // you happened to be in the previous one
  createEffect(on(() => [target().path, pattern()?.source], () => setCurrentMatch(0)))

  createEffect(on(() => [target().line, file.data?.path, rendered()], () => {
    const line = target().line
    if (!line || !file.data || rendered()) return
    queueMicrotask(() => code?.scrollToLine(line))
  }))

  createEffect(() => {
    try {
      localStorage.setItem(FONT_KEY, String(fontSize()))
    } catch { /* private mode; the size just won't stick ¯\_(ツ)_/¯ */ }
  })

  /* j/k (and the arrows) walk the code a line at a time, code search's
     "up/down one line in code browser" */
  function stepLine(delta: number) {
    const total = file.data?.lines ?? 0
    if (total === 0) return
    const next = Math.min(total, Math.max(1, (target().line ?? 1) + delta))
    openFile(target().repo, target().path, next, currentRef())
    code?.scrollToLine(next)
  }

  /*
    cmd/ctrl+a in a file selects the file, not the whole page around it - code search
    intercepts CTRL_A exactly like this. the gutter and the blame column are
    user-select: none, so what ends up on the clipboard is the code and nothing else
  */
  function selectAll(event: KeyboardEvent) {
    if (event.key !== 'a' || !(event.metaKey || event.ctrlKey) || event.altKey) return
    if (!content || inTextField(event.target)) return

    event.preventDefault()
    const range = document.createRange()
    range.selectNodeContents(content)
    const selection = getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  /*
    the panel is virtualised, so "everything" is only ever the few hundred rows that
    happen to be mounted. when the selection covers the whole container we know what
    was meant and hand over the file itself instead of the visible slice of it
  */
  function copyAll(event: ClipboardEvent) {
    const source = file.data?.content
    const selection = getSelection()
    if (!content || rendered() || source === undefined) return
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return

    const whole = document.createRange()
    whole.selectNodeContents(content)
    const current = selection.getRangeAt(0)
    if (current.compareBoundaryPoints(Range.START_TO_START, whole) > 0) return
    if (current.compareBoundaryPoints(Range.END_TO_END, whole) < 0) return

    event.preventDefault()
    event.clipboardData?.setData('text/plain', source)
  }

  onMount(() => {
    window.addEventListener('keydown', selectAll)
    window.addEventListener('copy', copyAll)
  })
  onCleanup(() => {
    window.removeEventListener('keydown', selectAll)
    window.removeEventListener('copy', copyAll)
  })

  function openFind(mode: TFindMode) {
    setFindMode(mode)
    if (mode === 'find') setFindText('')
  }

  function submitFind(backwards: boolean) {
    if (findMode() === 'goto') {
      const line = Number.parseInt(findText(), 10)
      if (Number.isFinite(line)) {
        openFile(target().repo, target().path, line)
        code?.scrollToLine(line)
      }
      setFindMode(null)
      return
    }
    step(backwards)
  }

  const permalink = () => `${location.origin}${location.pathname}${location.search}`

  /*
    "view raw" without a raw endpoint: the content is already here, and trpc rides a
    websocket so there's no url to link to anyway. the object url is released on a
    timer because nothing else will ever tell us the tab is gone
  */
  function openRaw() {
    const content = file.data?.content
    if (content === undefined) return
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }))
    window.open(url, '_blank', 'noopener')
    setTimeout(URL.revokeObjectURL, 60_000, url)
  }

  useShortcuts({
    'f': () => openFind('find'),
    'g': () => openFind('goto'),
    'n': () => step(false),
    'N': () => step(true),
    'y': () => setShowMatches(it => !it),
    'b': () => setShowBlame(it => !it),
    't': () => setShowTree(it => !it),
    'r': () => setRefPickerOpen(it => !it),
    'c': () => code?.scrollToLine(target().line ?? 1),
    '+': () => setFontSize(it => Math.min(MAX_FONT, it + 1)),
    '=': () => setFontSize(it => Math.min(MAX_FONT, it + 1)),
    '-': () => setFontSize(it => Math.max(MIN_FONT, it - 1)),
    '8': () => setColumnGuide(it => !it),
    'j': () => stepLine(1),
    'k': () => stepLine(-1),
    'ArrowDown': () => stepLine(1),
    'ArrowUp': () => stepLine(-1),
    'l': () => setLinksOpen(true),
    'l p': () => {
      setLinksOpen(false)
      void copy(target().path, 'path copied')
    },
    'l r': () => {
      setLinksOpen(false)
      void copy(`${permalink()}${target().line ? `#L${target().line}` : ''}`, 'link copied')
    },
    'Escape': () => setFindMode(null),
  })

  return (
    <div class="h-full flex overflow-hidden">
      <Show when={showTree()}>
        <div class="shrink-0 overflow-hidden border-r border-border" style={{ width: `${treeWidth()}px` }}>
          <FileTree repo={target().repo} activePath={target().path} />
        </div>
        <Splitter onResize={setTreeWidth} />
      </Show>

      <div class="min-w-0 flex flex-1 flex-col overflow-hidden">
        <div class="flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-2 py-1">
          <PathBreadcrumb repo={target().repo} path={target().path} />
          <div class="min-w-0 flex-1" />

          <RefPicker repo={target().repo} open={refPickerOpen()} onOpenChange={setRefPickerOpen} />

          <LinksMenu
            repo={target().repo}
            path={target().path}
            line={target().line}
            gitRef={currentRef() ?? 'HEAD'}
            remote={repo.data?.remote}
            open={linksOpen()}
            onOpenChange={setLinksOpen}
            onRaw={openRaw}
          />

          <Button
            variant="ghost"
            size="sm"
            class={cn('h-6 shrink-0 gap-1 px-1.5 text-xs', showBlame() && 'bg-accent')}
            title="blame (b)"
            onClick={() => setShowBlame(it => !it)}
          >
            <div class="i-lucide-user-round size-3.5" />
          </Button>

          <Show when={file.data?.comments?.length}>
            <Button
              variant="ghost"
              size="sm"
              class={cn('h-6 shrink-0 gap-1 px-1.5 text-xs', showComments() && 'bg-accent')}
              title="render markdown in doc comments on hover"
              onClick={() => setShowComments(it => !it)}
            >
              <div class="i-lucide-message-square-quote size-3.5" />
            </Button>
          </Show>

          <Show when={markdown() || ayaml() || woodpecker()}>
            <Button
              variant="ghost"
              size="sm"
              class="h-6 shrink-0 gap-1 px-1.5 text-xs"
              onClick={() => setShowRendered(it => !it)}
            >
              <div class={showRendered() ? 'i-lucide-code size-3.5' : 'i-lucide-file-text size-3.5'} />
              {showRendered() ? 'source' : 'rendered'}
            </Button>
          </Show>

          <Show when={file.data}>
            <div class="shrink-0 text-xs text-muted-foreground">
              {file.data!.lines.toLocaleString()}
              {' lines · '}
              {formatSize(file.data!.size)}
              {' · '}
              {file.data!.lang}
            </div>
          </Show>
        </div>

        <Show when={findMode()}>
          {mode => (
            <FindBar
              mode={mode()}
              value={findText()}
              total={matches().flat.length}
              current={currentMatch()}
              onInput={(value) => {
                setFindText(value)
                setCurrentMatch(0)
              }}
              onSubmit={submitFind}
              onClose={() => setFindMode(null)}
            />
          )}
        </Show>

        <Show when={blame.isError && showBlame()}>
          <div class="shrink-0 border-b border-border px-2 py-1 text-xs text-destructive">
            {(blame.error as Error | null)?.message ?? 'blame unavailable'}
          </div>
        </Show>

        <Switch>
          <Match when={file.isError}>
            <div class="flex flex-1 items-center justify-center text-sm text-destructive">
              {(file.error as Error | null)?.message}
            </div>
          </Match>

          <Match when={file.data}>
            <div ref={it => (content = it)} class="min-h-0 flex-1">
              <Show
                when={rendered()}
                fallback={(
                  <CodeBody
                    ref={it => (code = it)}
                    content={file.data!.content}
                    tokens={file.data!.tokens}
                    comments={showComments() ? file.data!.comments : undefined}
                    ranges={matches().ranges}
                    currentLine={currentHit()?.line}
                    currentMatch={currentHit()?.index}
                    activeLine={target().line}
                    blame={showBlame() ? blame.data : undefined}
                    repo={target().repo}
                    remote={repo.data?.remote}
                    onBlameBefore={sha => openRef(`${sha}^`)}
                    fontSize={fontSize()}
                    columnGuide={columnGuide()}
                    lineHref={line => routeToUrl({ ...target(), kind: 'VIEW', line, gitRef: currentRef() })}
                    onLineClick={line => openFile(target().repo, target().path, line, currentRef())}
                  />
                )}
              >
                <Switch>
                  <Match when={rendered() === 'markdown'}>
                    <div class="h-full overflow-auto">
                      {/* eslint-disable-next-line solid/no-innerhtml */}
                      <div class="markdown mx-auto max-w-3xl px-4 py-3" innerHTML={markdown()!} />
                    </div>
                  </Match>
                  <Match when={rendered() === 'ayaml'}>
                    <AYamlView config={ayaml()!} />
                  </Match>
                  <Match when={rendered() === 'woodpecker'}>
                    <WoodpeckerView config={woodpecker()!} />
                  </Match>
                </Switch>
              </Show>
            </div>
          </Match>

          <Match when={file.isPending}>
            <div class="flex flex-1 items-center justify-center text-sm text-muted-foreground">loading...</div>
          </Match>
        </Switch>
      </div>
    </div>
  )
}
